import { useEffect, useState, useCallback } from "react";
import { api, MODELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Briefcase, Plus, Trash2, Sparkles, Loader2, Building2, Coins, Clock, ClipboardList, Printer, Download, FileText, Users, Receipt, Milestone, Save, ArrowLeft } from "lucide-react";
import RichEditor from "@/components/RichEditor";
// Voice-to-draft removed per product decision.
import AIFullProject from "@/components/AIFullProject";

const STATUS = ["draft","proposed","active","completed","closed"];
const STATUS_COLOR = {
  draft:"bg-secondary text-foreground", proposed:"bg-amber-100 text-amber-900",
  active:"bg-emerald-100 text-emerald-900", completed:"bg-sky-100 text-sky-900", closed:"bg-neutral-200 text-neutral-700",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Consultancy() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null); // full doc when editing
  const load = useCallback(() => api.get("/consultancy").then(r => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = async () => {
    const { data } = await api.post("/consultancy", { title:"New consultancy engagement" });
    setActive(data); load();
  };
  const del = async (id) => { if (!window.confirm("Delete engagement?")) return; await api.delete(`/consultancy/${id}`); if (active?.id===id) setActive(null); load(); };

  if (active) return <ConsultancyDetail engagement={active} onBack={()=>{ setActive(null); load(); }} />;

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <div className="overline">Industry</div>
          <h1 className="font-display text-5xl mt-2 flex items-center gap-3"><Briefcase className="w-10 h-10 text-primary"/>Consultancy Works</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Manage industry engagements end-to-end — scope, statement of work, milestones, invoices, contacts and billable time. Print or download any as PDF / Word.</p>
        </div>
        <Button className="rounded-full" onClick={openNew} data-testid="btn-new-consultancy"><Plus className="w-4 h-4 mr-2"/>New engagement</Button>
      </div>

      {items.length === 0 ? (
        <div className="card-crisp rounded-lg p-10 text-center">
          <Briefcase className="w-10 h-10 text-primary mx-auto mb-3"/>
          <div className="font-display text-2xl">Track your consulting portfolio</div>
          <p className="text-muted-foreground mt-1">Add an engagement — pilot studies, industry advisory, contract research, expert testimony.</p>
          <Button className="mt-4 rounded-full" onClick={openNew}><Plus className="w-4 h-4 mr-2"/>Add first engagement</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(c => (
            <div key={c.id} className="card-crisp rounded-lg p-5 group hover:border-primary/60 cursor-pointer" data-testid={`consultancy-${c.id}`} onClick={()=>setActive(c)}>
              <div className="flex items-start justify-between mb-2">
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${STATUS_COLOR[c.status]||STATUS_COLOR.draft}`}>{c.status}</span>
                <button onClick={(e)=>{e.stopPropagation(); del(c.id);}} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive" data-testid={`consultancy-del-${c.id}`}><Trash2 className="w-4 h-4"/></button>
              </div>
              <div className="font-display text-xl leading-tight line-clamp-2">{c.title}</div>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Building2 className="w-3 h-3"/>{c.client || "—"}</div>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Coins className="w-3 h-3"/>{c.budget || "—"} · <Clock className="w-3 h-3"/>{c.duration || "—"}</div>
              <p className="text-sm text-muted-foreground mt-3 line-clamp-3">{c.scope || "—"}</p>
              <div className="mt-3 flex gap-2 text-[11px] text-muted-foreground">
                <span title="Milestones"><Milestone className="w-3 h-3 inline"/> {c.milestones?.length||0}</span>
                <span title="Invoices"><Receipt className="w-3 h-3 inline"/> {c.invoices?.length||0}</span>
                <span title="Contacts"><Users className="w-3 h-3 inline"/> {c.contacts?.length||0}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConsultancyDetail({ engagement, onBack }) {
  const [c, setC] = useState(engagement);
  const [saving, setSaving] = useState(false);
  const [ai, setAI] = useState({ busy:false, res:null, modelIdx:0 });

  const patch = (partial) => setC(prev => ({ ...prev, ...partial }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/consultancy/${c.id}`, c);
      setC(data); toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const download = async (fmt) => {
    // save first so exports reflect latest state
    await api.put(`/consultancy/${c.id}`, c);
    const token = localStorage.getItem("gf_token");
    const url = `${BACKEND}/api/consultancy/${c.id}/export/${fmt}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return toast.error(`Download failed (${r.status})`);
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(c.title||"consultancy").replace(/[^\w-]+/g,"_")}.${fmt}`;
    a.click();
  };

  const draftAI = async () => {
    setAI(a => ({...a, busy:true, res:null}));
    try {
      const m = MODELS[ai.modelIdx];
      const { data } = await api.post("/consultancy/ai/draft", { ...c, model_provider:m.provider, model_name:m.name });
      setAI(a => ({...a, res:data}));
      // Auto-populate SOW rich-text from AI
      const sow = `<h2>Executive summary</h2><p>${data.executive_summary||""}</p>`
        + `<h2>Methodology</h2><p>${data.methodology||""}</p>`
        + (data.deliverables?.length ? `<h2>Deliverables</h2><ul>${data.deliverables.map(d=>`<li>${d}</li>`).join("")}</ul>` : "")
        + (data.timeline?.length ? `<h2>Timeline</h2><ol>${data.timeline.map(t=>`<li>${t.phase} — ${t.weeks} weeks</li>`).join("")}</ol>` : "")
        + (data.budget_summary ? `<h2>Budget summary</h2><p>${data.budget_summary}</p>` : "")
        + (data.ip_and_confidentiality ? `<h2>IP & confidentiality</h2><p>${data.ip_and_confidentiality}</p>` : "")
        + (data.risks?.length ? `<h2>Risks & mitigations</h2><ul>${data.risks.map(r=>`<li>${r}</li>`).join("")}</ul>` : "");
      patch({ sow_html: sow });
      toast.success("AI Statement of Work drafted — review carefully before sending.");
    } catch { toast.error("AI draft failed"); }
    finally { setAI(a => ({...a, busy:false})); }
  };

  return (
    <div className="max-w-6xl mx-auto p-8 print:p-0">
      <div className="flex items-start justify-between gap-4 mb-6 print:hidden">
        <div>
          <Button variant="ghost" size="sm" onClick={onBack} className="mb-2" data-testid="cons-back"><ArrowLeft className="w-4 h-4 mr-1"/>Back to list</Button>
          <h1 className="font-display text-4xl flex items-center gap-3"><Briefcase className="w-8 h-8 text-primary"/>{c.title || "Untitled"}</h1>
          <div className="text-xs text-muted-foreground mt-1">Client: <b>{c.client||"—"}</b> · Status: <span className={`px-2 py-0.5 rounded-full ${STATUS_COLOR[c.status]||STATUS_COLOR.draft}`}>{c.status}</span></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <AIFullProject
            module="consultancy"
            title={c.title}
            defaultIdea={c.scope || ""}
            testidPrefix="cons-fp"
            docId={c.id}
            saveDoc={save}
            buttonLabel="Generate full consultancy"
            dialogTitle="Generate full consultancy engagement from an idea"
            onApply={({ suggested_title, sections }) => {
              const html = Object.entries(sections || {})
                .map(([k, v]) => v ? `<h2>${k.replace(/_/g,' ').replace(/\b\w/g, ch=>ch.toUpperCase())}</h2>${v}` : "")
                .join("");
              patch({
                title: (!c.title || c.title === "New consultancy engagement") ? (suggested_title || c.title) : c.title,
                sow_html: html || c.sow_html,
              });
            }}
          />
          <Button variant="outline" className="rounded-full" onClick={()=>window.print()} data-testid="cons-print"><Printer className="w-4 h-4 mr-2"/>Print</Button>
          <Button variant="outline" className="rounded-full" onClick={()=>download("pdf")} data-testid="cons-dl-pdf"><Download className="w-4 h-4 mr-2"/>PDF</Button>
          <Button variant="outline" className="rounded-full" onClick={()=>download("docx")} data-testid="cons-dl-docx"><Download className="w-4 h-4 mr-2"/>Word</Button>
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="cons-save">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Save className="w-4 h-4 mr-2"/>}Save</Button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="print:hidden flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="cons-tab-overview"><ClipboardList className="w-4 h-4 mr-1"/>Overview</TabsTrigger>
          <TabsTrigger value="sow" data-testid="cons-tab-sow"><FileText className="w-4 h-4 mr-1"/>Statement of Work</TabsTrigger>
          <TabsTrigger value="milestones" data-testid="cons-tab-milestones"><Milestone className="w-4 h-4 mr-1"/>Milestones</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="cons-tab-invoices"><Receipt className="w-4 h-4 mr-1"/>Invoices</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="cons-tab-contacts"><Users className="w-4 h-4 mr-1"/>Contacts</TabsTrigger>
          <TabsTrigger value="time" data-testid="cons-tab-time"><Clock className="w-4 h-4 mr-1"/>Time log</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="print:!block">
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="card-crisp rounded-lg p-5 space-y-3">
              <div><Label>Title</Label><Input value={c.title||""} onChange={(e)=>patch({title:e.target.value})} data-testid="cons-title"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Client</Label><Input value={c.client||""} onChange={(e)=>patch({client:e.target.value})}/></div>
                <div><Label>Industry</Label><Input value={c.industry||""} onChange={(e)=>patch({industry:e.target.value})} placeholder="Pharma, EdTech…"/></div>
                <div><Label>Duration</Label><Input value={c.duration||""} onChange={(e)=>patch({duration:e.target.value})} placeholder="8 weeks"/></div>
                <div><Label>Budget</Label><Input value={c.budget||""} onChange={(e)=>patch({budget:e.target.value})} placeholder="INR 8L"/></div>
              </div>
              <div><Label>Status</Label>
                <Select value={c.status||"draft"} onValueChange={(v)=>patch({status:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{STATUS.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Deliverables (comma separated)</Label>
                <Input value={(c.deliverables||[]).join(", ")} onChange={(e)=>patch({deliverables: e.target.value.split(",").map(s=>s.trim()).filter(Boolean)})}/>
              </div>
            </div>
            <div className="card-crisp rounded-lg p-5 space-y-3">
              <div><Label>Scope</Label><Textarea value={c.scope||""} onChange={(e)=>patch({scope:e.target.value})} className="min-h-[120px]"/></div>
              <div><Label>Internal notes</Label><Textarea value={c.notes||""} onChange={(e)=>patch({notes:e.target.value})} className="min-h-[80px]"/></div>
              <div className="border-t border-border pt-3 flex items-center justify-between">
                <div className="text-xs text-muted-foreground flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-primary"/>Draft SOW with AI</div>
                <div className="flex gap-2 items-center">
                  <Select value={String(ai.modelIdx)} onValueChange={(v)=>setAI(a=>({...a, modelIdx:Number(v)}))}><SelectTrigger className="w-56 h-8 text-xs"><SelectValue/></SelectTrigger><SelectContent>{MODELS.map((m,i)=><SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent></Select>
                  <Button size="sm" onClick={draftAI} disabled={ai.busy || !c.title} data-testid="cons-ai-draft">{ai.busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Sparkles className="w-4 h-4 mr-1"/>Draft SOW</>}</Button>
                  {/* Voice-to-draft removed */}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="sow">
          <div className="card-crisp rounded-lg p-5 mt-4" data-testid="cons-sow-editor">
            <div className="flex items-baseline justify-between mb-2">
              <div className="font-display text-2xl">Statement of Work</div>
              <div className="text-[11px] text-muted-foreground italic">AI may make mistakes — verify before sending to the client.</div>
            </div>
            <RichEditor value={c.sow_html||""} onChange={(html)=>patch({sow_html:html})}/>
          </div>
        </TabsContent>

        <TabsContent value="milestones">
          <MilestonesTab value={c.milestones||[]} onChange={(v)=>patch({milestones:v})}/>
        </TabsContent>
        <TabsContent value="invoices">
          <InvoicesTab value={c.invoices||[]} onChange={(v)=>patch({invoices:v})}/>
        </TabsContent>
        <TabsContent value="contacts">
          <ContactsTab value={c.contacts||[]} onChange={(v)=>patch({contacts:v})}/>
        </TabsContent>
        <TabsContent value="time">
          <TimeLogTab value={c.time_log||[]} onChange={(v)=>patch({time_log:v})}/>
        </TabsContent>
      </Tabs>

      {/* Print-only aggregated view */}
      <div className="hidden print:block print-page">
        <h1 className="font-display text-3xl mb-2">{c.title}</h1>
        <div className="text-sm mb-4">Client: {c.client} · Industry: {c.industry} · Duration: {c.duration} · Budget: {c.budget}</div>
        {c.scope && <><h2 className="font-display text-xl mt-3">Scope</h2><p>{c.scope}</p></>}
        {c.sow_html && <><h2 className="font-display text-xl mt-3">Statement of Work</h2><div dangerouslySetInnerHTML={{__html:c.sow_html}}/></>}
        {(c.milestones||[]).length>0 && <><h2 className="font-display text-xl mt-3">Milestones</h2><table className="w-full text-sm border"><thead><tr><th className="border p-1 text-left">Title</th><th className="border p-1 text-left">Due</th><th className="border p-1 text-left">Status</th></tr></thead><tbody>{c.milestones.map(m=><tr key={m.id}><td className="border p-1">{m.title}</td><td className="border p-1">{m.due}</td><td className="border p-1">{m.status}</td></tr>)}</tbody></table></>}
        <div className="mt-6 p-2 border border-dashed border-primary italic text-xs">AI may make mistakes — please review this document carefully before finalising.</div>
      </div>
    </div>
  );
}

function uid() { return Math.random().toString(36).slice(2,10); }

function MilestonesTab({ value, onChange }) {
  const add = () => onChange([...(value||[]), { id: uid(), title:"", due:"", status:"pending", notes:"" }]);
  const upd = (i, k, v) => { const n=[...value]; n[i]={...n[i], [k]:v}; onChange(n); };
  const del = (i) => onChange(value.filter((_,x)=>x!==i));
  return (
    <div className="card-crisp rounded-lg p-5 mt-4" data-testid="cons-milestones-tab">
      <div className="flex items-center justify-between mb-3"><div className="font-display text-2xl">Milestones</div><Button size="sm" onClick={add} className="rounded-full"><Plus className="w-4 h-4 mr-1"/>Add</Button></div>
      {value.length===0 ? <div className="text-sm text-muted-foreground">No milestones yet.</div> : (
        <div className="space-y-2">
          {value.map((m,i)=>(
            <div key={m.id} className="grid grid-cols-12 gap-2 items-end border-b border-border pb-2">
              <div className="col-span-4"><Label className="text-xs">Title</Label><Input value={m.title} onChange={(e)=>upd(i,"title",e.target.value)}/></div>
              <div className="col-span-2"><Label className="text-xs">Due</Label><Input type="date" value={m.due} onChange={(e)=>upd(i,"due",e.target.value)}/></div>
              <div className="col-span-2"><Label className="text-xs">Status</Label>
                <Select value={m.status} onValueChange={(v)=>upd(i,"status",v)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{["pending","in_progress","done","blocked"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="col-span-3"><Label className="text-xs">Notes</Label><Input value={m.notes} onChange={(e)=>upd(i,"notes",e.target.value)}/></div>
              <div className="col-span-1"><Button variant="ghost" size="sm" onClick={()=>del(i)}><Trash2 className="w-4 h-4 text-destructive"/></Button></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InvoicesTab({ value, onChange }) {
  const add = () => onChange([...(value||[]), { id: uid(), number:`INV-${String(value.length+1).padStart(3,"0")}`, date:"", amount:"", currency:"INR", description:"", status:"draft" }]);
  const upd = (i, k, v) => { const n=[...value]; n[i]={...n[i], [k]:v}; onChange(n); };
  const del = (i) => onChange(value.filter((_,x)=>x!==i));
  const total = value.reduce((s,i)=>s + (parseFloat(i.amount)||0), 0);
  return (
    <div className="card-crisp rounded-lg p-5 mt-4" data-testid="cons-invoices-tab">
      <div className="flex items-center justify-between mb-3">
        <div><div className="font-display text-2xl">Invoices</div><div className="text-xs text-muted-foreground">Total across all: <b>{total.toLocaleString()}</b></div></div>
        <Button size="sm" onClick={add} className="rounded-full"><Plus className="w-4 h-4 mr-1"/>Add invoice</Button>
      </div>
      {value.length===0 ? <div className="text-sm text-muted-foreground">No invoices yet.</div> : (
        <div className="space-y-2">
          {value.map((v,i)=>(
            <div key={v.id} className="grid grid-cols-12 gap-2 items-end border-b border-border pb-2">
              <div className="col-span-2"><Label className="text-xs">Number</Label><Input value={v.number} onChange={(e)=>upd(i,"number",e.target.value)}/></div>
              <div className="col-span-2"><Label className="text-xs">Date</Label><Input type="date" value={v.date} onChange={(e)=>upd(i,"date",e.target.value)}/></div>
              <div className="col-span-1"><Label className="text-xs">Curr.</Label>
                <Select value={v.currency} onValueChange={(x)=>upd(i,"currency",x)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{["INR","USD","EUR","GBP","AUD"].map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="col-span-2"><Label className="text-xs">Amount</Label><Input value={v.amount} onChange={(e)=>upd(i,"amount",e.target.value)}/></div>
              <div className="col-span-2"><Label className="text-xs">Status</Label>
                <Select value={v.status} onValueChange={(x)=>upd(i,"status",x)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{["draft","sent","paid","overdue"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="col-span-2"><Label className="text-xs">Description</Label><Input value={v.description} onChange={(e)=>upd(i,"description",e.target.value)}/></div>
              <div className="col-span-1"><Button variant="ghost" size="sm" onClick={()=>del(i)}><Trash2 className="w-4 h-4 text-destructive"/></Button></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactsTab({ value, onChange }) {
  const add = () => onChange([...(value||[]), { id: uid(), name:"", role:"", email:"", phone:"", notes:"" }]);
  const upd = (i, k, v) => { const n=[...value]; n[i]={...n[i], [k]:v}; onChange(n); };
  const del = (i) => onChange(value.filter((_,x)=>x!==i));
  return (
    <div className="card-crisp rounded-lg p-5 mt-4" data-testid="cons-contacts-tab">
      <div className="flex items-center justify-between mb-3"><div className="font-display text-2xl">Client contacts</div><Button size="sm" onClick={add} className="rounded-full"><Plus className="w-4 h-4 mr-1"/>Add contact</Button></div>
      {value.length===0 ? <div className="text-sm text-muted-foreground">No contacts yet.</div> : (
        <div className="space-y-2">
          {value.map((v,i)=>(
            <div key={v.id} className="grid grid-cols-12 gap-2 items-end border-b border-border pb-2">
              <div className="col-span-3"><Label className="text-xs">Name</Label><Input value={v.name} onChange={(e)=>upd(i,"name",e.target.value)}/></div>
              <div className="col-span-2"><Label className="text-xs">Role</Label><Input value={v.role} onChange={(e)=>upd(i,"role",e.target.value)}/></div>
              <div className="col-span-3"><Label className="text-xs">Email</Label><Input value={v.email} onChange={(e)=>upd(i,"email",e.target.value)}/></div>
              <div className="col-span-2"><Label className="text-xs">Phone</Label><Input value={v.phone} onChange={(e)=>upd(i,"phone",e.target.value)}/></div>
              <div className="col-span-1"><Label className="text-xs">Notes</Label><Input value={v.notes} onChange={(e)=>upd(i,"notes",e.target.value)}/></div>
              <div className="col-span-1"><Button variant="ghost" size="sm" onClick={()=>del(i)}><Trash2 className="w-4 h-4 text-destructive"/></Button></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimeLogTab({ value, onChange }) {
  const add = () => onChange([...(value||[]), { id: uid(), date:new Date().toISOString().slice(0,10), hours:1, activity:"", billable:true }]);
  const upd = (i, k, v) => { const n=[...value]; n[i]={...n[i], [k]:v}; onChange(n); };
  const del = (i) => onChange(value.filter((_,x)=>x!==i));
  const total = value.reduce((s,t)=>s+(parseFloat(t.hours)||0),0);
  const billable = value.filter(t=>t.billable).reduce((s,t)=>s+(parseFloat(t.hours)||0),0);
  return (
    <div className="card-crisp rounded-lg p-5 mt-4" data-testid="cons-time-tab">
      <div className="flex items-center justify-between mb-3">
        <div><div className="font-display text-2xl">Time log</div><div className="text-xs text-muted-foreground">Total <b>{total}</b>h · Billable <b>{billable}</b>h</div></div>
        <Button size="sm" onClick={add} className="rounded-full"><Plus className="w-4 h-4 mr-1"/>Add entry</Button>
      </div>
      {value.length===0 ? <div className="text-sm text-muted-foreground">No entries yet.</div> : (
        <div className="space-y-2">
          {value.map((t,i)=>(
            <div key={t.id} className="grid grid-cols-12 gap-2 items-end border-b border-border pb-2">
              <div className="col-span-2"><Label className="text-xs">Date</Label><Input type="date" value={t.date} onChange={(e)=>upd(i,"date",e.target.value)}/></div>
              <div className="col-span-1"><Label className="text-xs">Hours</Label><Input type="number" step="0.25" value={t.hours} onChange={(e)=>upd(i,"hours",parseFloat(e.target.value)||0)}/></div>
              <div className="col-span-6"><Label className="text-xs">Activity</Label><Input value={t.activity} onChange={(e)=>upd(i,"activity",e.target.value)}/></div>
              <div className="col-span-2 flex items-end gap-2 pb-1">
                <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={!!t.billable} onChange={(e)=>upd(i,"billable",e.target.checked)}/>Billable</label>
              </div>
              <div className="col-span-1"><Button variant="ghost" size="sm" onClick={()=>del(i)}><Trash2 className="w-4 h-4 text-destructive"/></Button></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
